import { compareSearch } from '@myre/common-primitives';
import { TagType } from '@myre/common-tag';
import { type SystemI18n } from '@myre/domain-i18n-wordings';
import { DynamicEnvService, openInNewTab } from '@myre/ui-domain-http';
import {
  ActionsDropdownComponent,
  DropdownItemType,
  type DropdownItem,
} from '@myre/ui-primitive-actions-dropdown';
import {
  AvatarComponent,
  ElementType,
  TagComponent,
} from '@myre/ui-primitive-components';
import { DisplayMode, MediaMatcherService } from '@myre/ui-primitive-core';
import { EllipsisDirective } from '@myre/ui-primitive-ellipsis';
import { DropdownComponent, type ISelectItem } from '@myre/ui-primitive-forms';
import {
  NavbarIconBtnComponent,
  NavbarSearchComponent,
} from '@myre/ui-primitive-navbar';

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  input,
  Output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';

import { AbstractHelpAnalyticsService } from '../../../analytics/abstract-help-analytics.service';
import { ChatWindowService } from '../../../chat/chat-window.service';
import { ChatService } from '../../../chat/chat.service';
import { WORDINGS } from '../../../myre-wordings-loader';

import { HeaderService } from '../header/header.service';
import { MenuService } from '../menu/menu.service';
import { RoutingService } from '../routing/routing.service';

/** News section of the help app, as routed by its Docusaurus sidebar. */
const HELP_NEWS_PATH = '/docs/category/releases';

@Component({
  selector: 'myre-header',
  templateUrl: './myre-header.component.html',
  styleUrls: ['./myre-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ActionsDropdownComponent,
    AvatarComponent,
    DropdownComponent,
    EllipsisDirective,
    FormsModule,
    NavbarIconBtnComponent,
    NavbarSearchComponent,
    TagComponent,
  ],
})
export class MyreHeaderComponent<T> {
  @Input({
    required: true,
  })
  public auth$: (jwt?: string) => Observable<boolean>;

  public searchResults = input.required<ISelectItem<T>[]>();

  @Output()
  public searchUserInput = new EventEmitter<string>();

  @Output()
  public searchItemClicked = new EventEmitter<T>();

  protected get _menuOpened(): boolean {
    return this._menuService.opened();
  }

  protected get _chatOpened(): boolean {
    return !!this._chatWindowService.activeWindow();
  }

  protected readonly _headerService = inject(HeaderService);
  protected readonly _routingService = inject(RoutingService);
  protected readonly _menuService = inject(MenuService);
  protected readonly _chatService = inject(ChatService);
  private readonly _chatWindowService = inject(ChatWindowService);
  protected readonly _mediaMatcherService = inject(MediaMatcherService);
  private readonly _dynamicEnvService = inject(DynamicEnvService);
  private readonly _wordings = inject<SystemI18n>(WORDINGS);
  private readonly _abstractHelpAnalyticsService = inject(
    AbstractHelpAnalyticsService,
    {
      optional: true,
    },
  );

  protected readonly _helpDropdownItems: DropdownItem[] = [
    {
      type: DropdownItemType.TextWithIconsOrTags,
      items: [
        {
          mainIcon: 'myre-icon-library_books',
          label: this._wordings._system.help.documentation(),
          onClick: () => this._openUserGuide(),
          elements: {
            type: ElementType.Icon,
            name: 'myre-icon-launch',
          },
        },
        {
          mainIcon: 'myre-icon-campaign',
          label: this._wordings._system.help.changelog(),
          onClick: () => this._openReleaseNotes(),
          elements: {
            type: ElementType.Icon,
            name: 'myre-icon-launch',
          },
        },
      ],
    },
  ];

  protected readonly _searchInput = signal<string>('');

  protected readonly _isSmallDisplay = computed<boolean>(
    () => this._mediaMatcherService.currentDisplayMode() === DisplayMode.Small,
  );

  protected readonly _smallSearchOpened = signal<boolean>(false);

  /**
   * On small display the opened search takes the whole header row, so its
   * results panel spans the screen width instead of overflowing it.
   */
  protected readonly _smallSearchExpanded = computed<boolean>(
    () => this._isSmallDisplay() && this._smallSearchOpened(),
  );

  /**
   * Small display collapses the help, settings and avatar menus into the
   * hamburger so the header doesn't spread every icon across the row.
   */
  protected readonly _smallMenuDropdownItems = computed<DropdownItem[]>(() => {
    return [
      ...this._helpDropdownItems,
      ...(this._headerService.settings() ?? []),
      ...(this._headerService.avatar()?.dropdownItems ?? []),
    ];
  });

  protected readonly _dropdownItems = computed<ISelectItem[]>(() => {
    return (this._headerService.dropdownItems() ?? [])?.reduce((acc, item) => {
      if (
        this._searchInput() === '' ||
        compareSearch(item.label, this._searchInput())
      ) {
        acc.push({
          label: item.label,
          value: item.id,
        });
      }

      return acc;
    }, []);
  });

  protected _selectedItem(id: number): void {
    const item = this._headerService.dropdownItems().find(i => i.id === id);

    if (item.onClick) {
      item.onClick();
    }

    if (typeof item.id === 'number') {
      this._routingService.navigateToCompany({
        commands: [item.id.toString()],
      });
    }
  }

  protected _onHomeClick(): void {
    const item = this._headerService.selectedDropdownItem();

    if (item) {
      this._routingService.navigateToCompany({
        commands: [item],
      });

      return;
    }

    this._routingService.navigateToRoot({
      commands: ['/'],
    });
  }

  private _openUserGuide(): void {
    this._abstractHelpAnalyticsService?.trackUserGuideOpened({
      source: 'header',
    });
    this._openHelp();
  }

  private _openReleaseNotes(): void {
    this._abstractHelpAnalyticsService?.trackReleaseNotesOpened();
    this._openHelp(HELP_NEWS_PATH);
  }

  private _openHelp(path: string = ''): void {
    openInNewTab(`${this._dynamicEnvService.env.myre.help.front.host}${path}`);
  }

  protected _toggleMenu(): void {
    this._menuService.toggle();
  }

  protected _toggleChat(): void {
    this._chatWindowService.toggleChat();
  }

  protected readonly TagType = TagType;
}
